import Ref from './Ref';
import RefSet from './RefSet';
import RefMap from './RefMap';

import hash from './hash';

export { Id } from './Id';
export { Ref } from './Ref';
export { RefMap } from './RefMap';
export { RefSet } from './RefSet';
export { SoftCacheFn } from './SoftCacheFn';

(window as any).hash = hash; // tslint:disable-line

export function assert(x: boolean) {
  if (!x) {
    throw new Error('Assertion failure');
  }
}

export function assertType<T>(x: T) { return x; }

export function range(n: number): number[] {
  return (new Array(n)).fill(0).map((_, i) => i);
}

export function NormalRand(uniformRand: () => number): () => number {
  function sample(): number {
    const u = uniformRand() * 2 - 1;
    const v = uniformRand() * 2 - 1;

    const r = u * u + v * v;

    if (r === 0 || r > 1) {
      return sample();
    }

    const c = Math.sqrt(-2 * Math.log(r) / r);

    return u * c;
  }

  return sample;
}

export type Pair<T0, T1> = { 0: T0, 1: T1 };

export function Pair<T0, T1>(t0: T0, t1: T1) {
  return { 0: t0, 1: t1 };
}

export type TwinPair<T> = Pair<T, T>;

export function TwinPair<T>(a: T, b: T, offset: 0 | 1 = 0) {
  return offset === 0 ? Pair(a, b) : Pair(b, a);
}

export namespace TwinPair {
  export function map<T, V>(pair: TwinPair<T>, mapper: (x: T) => V): TwinPair<V> {
    return TwinPair<V>(
      mapper(pair[0]),
      mapper(pair[1]),
    );
  }

  export function fromIndexMapper<T>(mapper: (index: 0 | 1) => T): TwinPair<T> {
    return TwinPair<T>(
      mapper(0),
      mapper(1),
    );
  }
}

export function uniq<T>(items: T[]): T[] {
  const result = [];

  for (let i = 0; i < items.length; i += 1) {
    if (i === 0 || items[i] !== items[i - 1]) {
      result.push(items[i]);
    }
  }

  return result;
}

export function getNumbering<T extends { ref: Ref<T> }>(
  set: RefSet<T>
): {
  fromNumber: Ref<T>[],
  toNumber: RefMap<T, number>,
  size: number,
} {
  const refs: Ref<T>[] = RefSet.Refs(set);

  const numberMap: RefMap<T, number> = refs.reduce(
    (map, ref, i) => RefMap.set(map, ref, i),
    RefMap<T, number>()
  );

  return {
    fromNumber: refs,
    toNumber: numberMap,
    size: refs.length,
  };
}

export function bothOrNull<T0, T1>(
  pair: Pair<T0 | null, T1 | null>,
): Pair<T0, T1> | null {
  const { 0: a, 1: b } = pair;

  if (a === null || b === null) {
    return null;
  }

  return Pair(a, b);
}

export function swap<T0, T1>(pair: Pair<T0, T1>): Pair<T1, T0> {
  const { 0: a, 1: b } = pair;
  return Pair(b, a);
}

export function ObjectKeys<T>(obj: T): (keyof T)[] {
  return Object.keys(obj) as (keyof T)[];
}

export function notNull<T>(x: T): x is Exclude<T, null> {
  return x !== null;
}

export function approach({
  value,
  target,
  dt,
  decayPeriod,
  epsilon,
}: {
  value: number,
  target: number,
  dt: number,
  decayPeriod: number,
  epsilon: number
}) {
  const keepRatio = Math.exp(-dt / decayPeriod);
  return keepRatio * value + (1 - keepRatio) * target;
}



// WEBPACK FOOTER //
// ./src/util/index.ts